import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { CalendarClock, CheckCircle2, Loader2, Repeat, List, CalendarDays, ChevronLeft, ChevronRight, History, Undo2, ChevronDown } from "lucide-react";
import { api, formatMXN } from "@/lib/api";
import { FREQUENCIES } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const freqLabel = (v) => FREQUENCIES.find((f) => f.value === v)?.label || v;
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DOW = ["L", "M", "M", "J", "V", "S", "D"];

function urgency(dateStr) {
  if (!dateStr) return { label: "Sin fecha", cls: "bg-slate-500/15 text-slate-400 border-slate-500/30" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + "T00:00:00");
  const days = Math.round((due - today) / 86400000);
  if (days < 0) return { label: `Vencido (${Math.abs(days)}d)`, cls: "bg-rose-500/15 text-rose-500 border-rose-500/30" };
  if (days === 0) return { label: "Vence hoy", cls: "bg-rose-500/15 text-rose-500 border-rose-500/30" };
  if (days <= 3) return { label: `En ${days} día${days > 1 ? "s" : ""}`, cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" };
  return { label: `En ${days} días`, cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" };
}

function CalendarView({ items }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = new Date().toISOString().slice(0, 10);

  const byDay = {};
  items.forEach((m) => {
    if (!m.next_due_date) return;
    const [y, mo] = m.next_due_date.split("-").map(Number);
    if (y === year && mo === month + 1) {
      const day = Number(m.next_due_date.split("-")[2]);
      (byDay[day] = byDay[day] || []).push(m);
    }
  });

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg">{MONTHS[month]} {year}</h3>
        <div className="flex gap-1">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="w-9 h-9 rounded-lg border border-border hover:bg-accent flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="w-9 h-9 rounded-lg border border-border hover:bg-accent flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-1">
        {DOW.map((d, i) => <div key={i} className="text-xs font-semibold text-muted-foreground py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const dayItems = byDay[d] || [];
          const isToday = dateStr === todayStr;
          const total = dayItems.reduce((s, m) => s + m.amount, 0);
          return (
            <div key={i} className={`min-h-[64px] rounded-lg border p-1.5 text-left ${isToday ? "border-emerald-500/50 bg-emerald-500/5" : "border-border"} ${dayItems.length ? "bg-rose-500/[0.04]" : ""}`}>
              <div className={`text-xs font-numeric ${isToday ? "text-emerald-500 font-bold" : "text-muted-foreground"}`}>{d}</div>
              {dayItems.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  <div className="text-[10px] font-numeric font-semibold text-rose-500 truncate">{formatMXN(total)}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{dayItems[0].category}{dayItems.length > 1 ? ` +${dayItems.length - 1}` : ""}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function UpcomingPayments() {
  const qc = useQueryClient();
  const [view, setView] = useState("list");
  const [openHistory, setOpenHistory] = useState({});
  const [busy, setBusy] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["upcoming"], queryFn: async () => (await api.get("/upcoming-payments")).data });

  const markPaid = async (id) => {
    setBusy(id);
    try { await api.post(`/movements/${id}/mark-paid`); toast.success("Pagado. Se registró el movimiento To y avanzó la fecha."); qc.invalidateQueries(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
    finally { setBusy(null); }
  };
  const undoPaid = async (id) => {
    setBusy(id);
    try { await api.post(`/movements/${id}/undo-payment`); toast.success("Último pago deshecho. Volvió a pendiente."); qc.invalidateQueries(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo deshacer"); }
    finally { setBusy(null); }
  };
  const toggleHistory = (id) => setOpenHistory((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="py-4" data-testid="upcoming-payments-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><CalendarClock className="w-6 h-6 text-emerald-500" /><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Próximos pagos</h1></div>
          <p className="text-muted-foreground text-sm mt-1">Al marcar pagado se registra un movimiento To. Los pagos son secuenciales y puedes deshacer el último.</p>
        </div>
        <div className="flex rounded-xl border border-border p-1 bg-secondary/40">
          <button data-testid="view-list" onClick={() => setView("list")} className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 ${view === "list" ? "bg-card shadow-sm" : "text-muted-foreground"}`}><List className="w-4 h-4" /> Lista</button>
          <button data-testid="view-calendar" onClick={() => setView("calendar")} className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 ${view === "calendar" ? "bg-card shadow-sm" : "text-muted-foreground"}`}><CalendarDays className="w-4 h-4" /> Calendario</button>
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
        ) : data.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center">
            <Repeat className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No tienes pagos recurrentes. Marca un movimiento como recurrente en Registro rápido.</p>
          </div>
        ) : view === "calendar" ? (
          <CalendarView items={data} />
        ) : (
          <div className="space-y-3" data-testid="upcoming-list">
            {data.map((m, i) => {
              const u = urgency(m.next_due_date);
              const history = [...(m.payment_history || [])].reverse();
              const isCredit = !!m.credit_account_id;
              const showHist = openHistory[m.id];
              return (
                <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-rose-500/10 flex items-center justify-center shrink-0"><Repeat className="w-5 h-5 text-rose-500" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{m.category}</p>
                      <p className="text-xs text-muted-foreground">{freqLabel(m.frequency)} · Próximo: <span className="font-numeric">{m.next_due_date || "—"}</span>{m.responsible ? ` · ${m.responsible}` : ""}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${u.cls}`}>{u.label}</span>
                    <span className="font-numeric font-bold text-rose-500 sm:w-28 sm:text-right">{formatMXN(m.amount)}</span>
                    <div className="flex gap-2 shrink-0">
                      <Button data-testid={`mark-paid-${m.id}`} onClick={() => markPaid(m.id)} disabled={busy === m.id} variant="outline" className="rounded-xl gap-2">
                        {busy === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />} Pagado
                      </Button>
                      {!isCredit && (history.length > 0) && (
                        <Button data-testid={`undo-paid-${m.id}`} onClick={() => undoPaid(m.id)} disabled={busy === m.id} variant="ghost" size="icon" title="Deshacer último pago"
                          className="rounded-xl text-muted-foreground hover:text-amber-500"><Undo2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                  {!isCredit && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <button data-testid={`toggle-history-${m.id}`} onClick={() => toggleHistory(m.id)}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                        <History className="w-3.5 h-3.5" /> Historial de pagos ({history.length})
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showHist ? "rotate-180" : ""}`} />
                      </button>
                      {showHist && (
                        <div className="mt-2 space-y-1" data-testid={`history-${m.id}`}>
                          {history.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-1">Aún no hay pagos registrados.</p>
                          ) : history.map((h) => (
                            <div key={h.id} className="flex items-center justify-between text-xs rounded-lg bg-secondary/40 px-3 py-1.5">
                              <span className="text-muted-foreground">Periodo <span className="font-numeric text-foreground">{h.due_date}</span> · pagado <span className="font-numeric text-foreground">{h.paid_date}</span></span>
                              <span className="font-numeric font-semibold text-rose-500">{formatMXN(h.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
